import { createAction } from '@reduxjs/toolkit';

import type { GroundhogAnalysisResponse, LanguageEnum } from 'js/src/client';
import type {
    DOC_TO_SCRIPT_PAGE,
    DocToScriptData,
    RawScriptOption,
    Script,
    ScriptOption,
    ScriptOptionDurationValue,
    ScriptOptionsLoadingStatus,
} from 'js/src/data/script-composer/script-composer-types';
import { SCRIPT_COMPOSER_ACTIONS } from 'js/src/data/script-composer/script-composer-types';
import type { SCRIPT_CREATION_SOURCES } from 'js/src/libs/consts';

export const setDocToScriptPage = createAction<{
    page: DOC_TO_SCRIPT_PAGE | null;
}>(SCRIPT_COMPOSER_ACTIONS.SET_CURRENT_DOC_TO_SCRIPT_PAGE);

export const setDocToScriptData = createAction<DocToScriptData>(
    SCRIPT_COMPOSER_ACTIONS.SET_DOC_TO_SCRIPT_DATA,
);

export const setDocToScriptImportType = createAction<SCRIPT_CREATION_SOURCES>(
    SCRIPT_COMPOSER_ACTIONS.SET_DOC_TO_SCRIPT_IMPORT_TYPE,
);

export const setSelectedScriptOption = createAction<{
    scriptOption: ScriptOption | null;
}>(SCRIPT_COMPOSER_ACTIONS.SET_SELECTED_SCRIPT_OPTION);

export const setScriptForScriptOption = createAction<{
    scriptOptionId: string;
    scriptData: Script;
}>(SCRIPT_COMPOSER_ACTIONS.SET_SCRIPT_FOR_SCRIPT_OPTION);

export const setEditedScript = createAction<{ scriptData: Script }>(
    SCRIPT_COMPOSER_ACTIONS.SET_EDITED_SCRIPT,
);

export const setEditedScriptInitialWordCount = createAction<{
    editedScriptInitialWordCount: number;
}>(SCRIPT_COMPOSER_ACTIONS.SET_EDITED_SCRIPT_INITIAL_WORD_COUNT);

export const setEditedScriptLanguage = createAction<LanguageEnum>(
    SCRIPT_COMPOSER_ACTIONS.SET_EDITED_SCRIPT_LANGUAGE,
);

export const removeScriptFromCache = createAction<{
    scriptOptionId: string;
}>(SCRIPT_COMPOSER_ACTIONS.REMOVE_SCRIPT_FROM_CACHE);

export const resetScriptCache = createAction(SCRIPT_COMPOSER_ACTIONS.RESET_SCRIPT_CACHE);

export const setDocToScriptOptions = createAction<{
    scriptOptions: RawScriptOption[];
    scriptId: string;
}>(SCRIPT_COMPOSER_ACTIONS.SET_DOC_TO_SCRIPT_OPTIONS_DATA);

export const setIsFetchingScriptOptionAIScript = createAction<{
    scriptOptionId: string;
    isLoading: boolean;
}>(SCRIPT_COMPOSER_ACTIONS.SET_IS_FETCHING_SCRIPT_OPTION_AI_SCRIPT);

export const setIsFetchingScriptFromIdea = createAction<boolean>(
    SCRIPT_COMPOSER_ACTIONS.SET_IS_FETCHING_SCRIPT_FROM_IDEA,
);

export const updateDocToScriptOption = createAction<{
    scriptOption: ScriptOption;
    scriptId: string;
}>(SCRIPT_COMPOSER_ACTIONS.UPDATE_DOC_TO_SCRIPT_OPTION);

export const setIsFetchingAIScriptOptions = createAction<ScriptOptionsLoadingStatus>(
    SCRIPT_COMPOSER_ACTIONS.SET_IS_FETCHING_AI_SCRIPT_OPTIONS,
);

export const setLongestSupportedScriptDuration = createAction<number | null>(
    SCRIPT_COMPOSER_ACTIONS.SET_LONGEST_SUPPORTED_SCRIPT_DURATION,
);
export const setCurrentAIAvatarVoicePreset = createAction<number>(
    SCRIPT_COMPOSER_ACTIONS.SET_CURRENT_AI_AVATAR_VOICE_PRESET,
);
export const setCurrentAIAvatar = createAction<number>(
    SCRIPT_COMPOSER_ACTIONS.SET_CURRENT_AI_AVATAR,
);
export const setCurrentAIVoice = createAction<string>(SCRIPT_COMPOSER_ACTIONS.SET_CURRENT_AI_VOICE);
export const setIdeaOutlineSubject = createAction<string>(
    SCRIPT_COMPOSER_ACTIONS.SET_IDEA_OUTLINE_SUBJECT,
);
export const setIdeaOutlineReference = createAction<{
    index: number;
    reference: string;
}>(SCRIPT_COMPOSER_ACTIONS.SET_IDEA_OUTLINE_REFERENCES);
export const addIdeaOutlineReference = createAction(
    SCRIPT_COMPOSER_ACTIONS.ADD_IDEA_OUTLINE_REFERENCE,
);
export const deleteIdeaOutlineReference = createAction<number>(
    SCRIPT_COMPOSER_ACTIONS.DELETE_IDEA_OUTLINE_REFERENCE,
);
export const setIsUploadingScriptImages = createAction<boolean>(
    SCRIPT_COMPOSER_ACTIONS.SET_IS_UPLOADING_SCRIPT_IMAGES,
);
export const setTargetDuration = createAction<ScriptOptionDurationValue>(
    SCRIPT_COMPOSER_ACTIONS.SET_TARGET_DURATION,
);
export const setGroundhogAnalysis = createAction<GroundhogAnalysisResponse | null>(
    SCRIPT_COMPOSER_ACTIONS.SET_GROUNDHOG_ANALYSIS,
);
export const clearGroundhogAnalysisForText = createAction<{
    text: string;
}>(SCRIPT_COMPOSER_ACTIONS.CLEAR_GROUNDHOG_ANALYSIS_FOR_TEXT);
export const setSelectedBlockText = createAction<string | null>(
    SCRIPT_COMPOSER_ACTIONS.SET_SELECTED_BLOCK_TEXT,
);

export const setIsConvertingToVoiceover = createAction<boolean>(
    SCRIPT_COMPOSER_ACTIONS.SET_IS_CONVERTING_TO_VOICEOVER,
);
